import React, { useState, useEffect } from "react";
import { Modal, Stepper, Button, Group, LoadingOverlay } from "@mantine/core";
import { ServiceAdjustmentForm } from "../../types/serviceAdjustmentForms";
import { useServiceAdjustmentForms } from "../../context/ServiceAdjustmentFormsContext";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import AdjustmentFormConfigStep from "./AdjustmentFormConfigStep";
import AdjustmentFormServicesStep from "./AdjustmentFormServicesStep";
import { notifyError, notifySuccess } from "../../utils/notify";

interface AdjustmentFormWizardProps {
  isOpen: boolean;
  onClose: () => void;
  initialData: ServiceAdjustmentForm | null;
  onSuccess: () => void;
}

function AdjustmentFormWizard({
  isOpen,
  onClose,
  initialData,
  onSuccess,
}: AdjustmentFormWizardProps) {
  const {
    upsertAdjustmentForm,
    connectFormToServices,
    disconnectFormFromService,
    fetchFormJunctions,
  } = useServiceAdjustmentForms();

  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [initialServiceIds, setInitialServiceIds] = useState<string[]>([]);
  const [initialFormData, setInitialFormData] = useState<ServiceAdjustmentForm>(
    {
      service_adjustment_form_id: null,
      name: "",
      description: null,
      priority: 1,
      is_active: true,
      questions: [],
    },
  );

  const [formData, setFormData] = useState<ServiceAdjustmentForm>({
    service_adjustment_form_id: null,
    name: "",
    description: null,
    priority: 1,
    is_active: true,
    questions: [],
  });

  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);

  // Load initial data when editing
  useEffect(() => {
    if (isOpen && initialData) {
      // Deep clone the initial data to avoid reference issues
      // Clone BOTH formData and initialFormData to prevent mutation
      const clonedDataForForm = JSON.parse(JSON.stringify(initialData));
      const clonedDataForInitial = JSON.parse(JSON.stringify(initialData));
      setFormData(clonedDataForForm);
      setInitialFormData(clonedDataForInitial);
      // Fetch existing junctions for this form
      if (initialData.service_adjustment_form_id) {
        fetchFormJunctions(initialData.service_adjustment_form_id).then(
          (junctions) => {
            const serviceIds = junctions.map((j) => j.salon_service_id);
            setSelectedServiceIds([...serviceIds]);
            setInitialServiceIds([...serviceIds]);
          },
        );
      }
    } else if (isOpen && !initialData) {
      // Reset for new form
      const emptyForm = {
        service_adjustment_form_id: null,
        name: "",
        description: null,
        priority: 1,
        is_active: true,
        questions: [],
      };
      setFormData(JSON.parse(JSON.stringify(emptyForm)));
      setInitialFormData(JSON.parse(JSON.stringify(emptyForm)));
      setSelectedServiceIds([]);
      setInitialServiceIds([]);
    }
  }, [isOpen, initialData, fetchFormJunctions]);

  // Reset wizard when closed
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      setErrors({});
      // Reset form data to initial values
      setFormData(JSON.parse(JSON.stringify(initialFormData)));
      setSelectedServiceIds([...initialServiceIds]);
    }
  }, [isOpen, initialFormData, initialServiceIds]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleQuestionsChange = (questions: any[]) => {
    setFormData((prev) => ({
      ...prev,
      questions,
    }));
  };

  const handleToggleService = (serviceId: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId],
    );
  };

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Form name is required";
    }

    if (formData.priority < 1) {
      newErrors.priority = "Priority must be at least 1";
    }

    // Validate questions
    if (formData.questions.length > 0) {
      const hasInvalidQuestions = formData.questions.some(
        (q) => !q.question.trim() || q.options.length === 0,
      );
      if (hasInvalidQuestions) {
        newErrors.questions =
          "All questions must have text and at least one answer option";
      }

      // Validate that all options have answer text
      const hasInvalidOptions = formData.questions.some((q) =>
        q.options.some((opt) => !opt.answer.trim()),
      );
      if (hasInvalidOptions) {
        newErrors.questions = "All answer options must have text";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 0) {
      if (validateStep1()) {
        setCurrentStep(1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      // Step 1: Upsert the form
      const savedForm = await upsertAdjustmentForm(formData);
      const formId =
        savedForm.service_adjustment_form_id ||
        formData.service_adjustment_form_id;

      if (!formId) {
        throw new Error("Failed to get form ID after save");
      }

      // Step 2: Handle service connections
      if (initialData) {
        // Editing: Compare and update junctions
        const servicesToConnect = selectedServiceIds.filter(
          (id) => !initialServiceIds.includes(id),
        );
        const servicesToDisconnect = initialServiceIds.filter(
          (id) => !selectedServiceIds.includes(id),
        );

        // Get junctions to disconnect
        const allJunctions = await fetchFormJunctions(formId);
        const junctionsToDisconnect = allJunctions
          .filter((j) => servicesToDisconnect.includes(j.salon_service_id))
          .map((j) => j.service_adjustment_form_junction_id);

        // Parallelize connects and disconnects
        const promises = [
          connectFormToServices(formId, servicesToConnect),
          ...junctionsToDisconnect.map((junctionId) =>
            disconnectFormFromService(junctionId),
          ),
        ];

        if (promises.length > 0) {
          await Promise.all(promises);
        }
      } else {
        // Creating: Just connect selected services
        if (selectedServiceIds.length > 0) {
          await connectFormToServices(formId, selectedServiceIds);
        }
      }

      notifySuccess(
        `Adjustment form ${initialData ? "updated" : "created"} successfully`,
        { title: "Success" },
      );

      onSuccess();
    } catch (error) {
      console.error("Error saving form:", error);
      notifyError(
        error instanceof Error
          ? error.message
          : "Failed to save adjustment form",
        { title: "Error" },
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to deep compare questions including all nested options
  const questionsHaveChanged = () => {
    if (formData.questions.length !== initialFormData.questions.length) {
      return true;
    }

    for (let i = 0; i < formData.questions.length; i++) {
      const current = formData.questions[i];
      const initial = initialFormData.questions[i];

      // Compare question text and priority
      if (
        current.question !== initial.question ||
        current.priority !== initial.priority
      ) {
        return true;
      }

      // Compare options length
      if (current.options.length !== initial.options.length) {
        return true;
      }

      // Compare each option
      for (let j = 0; j < current.options.length; j++) {
        const currentOption = current.options[j];
        const initialOption = initial.options[j];

        if (
          currentOption.answer !== initialOption.answer ||
          currentOption.priority !== initialOption.priority ||
          currentOption.duration_delta_mins !==
            initialOption.duration_delta_mins ||
          currentOption.price_delta !== initialOption.price_delta
        ) {
          return true;
        }
      }
    }

    return false;
  };

  const handleCloseAttempt = () => {
    // Check if there are unsaved changes by comparing with initial state
    const hasFormChanges =
      formData.name !== initialFormData.name ||
      formData.description !== initialFormData.description ||
      formData.priority !== initialFormData.priority ||
      formData.is_active !== initialFormData.is_active ||
      questionsHaveChanged();

    const hasServiceChanges =
      JSON.stringify([...selectedServiceIds].sort()) !==
      JSON.stringify([...initialServiceIds].sort());

    if (hasFormChanges || hasServiceChanges) {
      setShowDiscardModal(true);
    } else {
      onClose();
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardModal(false);
    // Reset form data to initial values before closing
    setFormData(JSON.parse(JSON.stringify(initialFormData)));
    setSelectedServiceIds([...initialServiceIds]);
    onClose();
  };

  return (
    <>
      <Modal
        opened={isOpen}
        onClose={handleCloseAttempt}
        title={initialData ? "Edit Adjustment Form" : "Create Adjustment Form"}
        size="xl"
      >
        <LoadingOverlay visible={isLoading} />

        <Stepper active={currentStep} onStepClick={setCurrentStep}>
          <Stepper.Step
            label="Configure Form"
            description="Setup form and questions"
          >
            <AdjustmentFormConfigStep
              formData={formData}
              onChange={handleFieldChange}
              onQuestionsChange={handleQuestionsChange}
              errors={errors}
            />
          </Stepper.Step>

          <Stepper.Step label="Connect Services" description="Link to services">
            <AdjustmentFormServicesStep
              selectedServiceIds={selectedServiceIds}
              onToggleService={handleToggleService}
            />
          </Stepper.Step>
        </Stepper>

        <Group justify="space-between" mt="xl">
          {currentStep === 0 ? (
            <Button variant="default" onClick={handleCloseAttempt}>
              Cancel
            </Button>
          ) : (
            <Button variant="default" onClick={handleBack}>
              Back
            </Button>
          )}

          {currentStep === 0 ? (
            <Button onClick={handleNext} color="teal">
              Next
            </Button>
          ) : (
            <Button onClick={handleSave} color="teal" loading={isLoading}>
              Save
            </Button>
          )}
        </Group>
      </Modal>

      <DeleteConfirmationModal
        isOpen={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        onConfirm={handleDiscardConfirm}
        title="Discard Changes"
        message="Are you sure you want to discard your changes? This action cannot be undone."
      />
    </>
  );
}

export default AdjustmentFormWizard;
